import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAuth } from 'modules/auth/auth.context';
import {
  ControlledDatePicker,
  ControlledFileUpload,
  ControlledSelect,
  ControlledTextField,
  ModalWrapper,
  SectionTitle,
} from 'modules/shared/components';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import { EXPENSE_TYPE_OPTIONS, VOUCHER_REQUEST_DEFAULT_VALUES } from '../constants';
import { requestVoucher } from '../service';
import { voucherRequestValidation } from '../utils';

/**
 * Voucher Request Modal
 * @param {Object} props
 * @param {Function} props.onRefresh - Callback function to refresh the voucher request list
 * @returns {JSX.Element}
 */
export default function VoucherRequestModal({ onRefresh = () => {} }: { onRefresh?: () => void }) {
  const { user } = useAuth();
  const employeeId = user?.employee_id;

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const methods = useForm({ defaultValues: VOUCHER_REQUEST_DEFAULT_VALUES });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (!open) return;
    reset(VOUCHER_REQUEST_DEFAULT_VALUES);
  }, [open, reset]);

  const submitVoucherRequest = async (data) => {
    if (!employeeId) return;
    setSaving(true);
    try {
      await requestVoucher(employeeId, data);
      toast.success('Voucher requested successfully');
      setOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to request voucher',
          error: err,
          code: 'VOUCHER_REQUEST_FAILED',
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type='button'
        className='btn cta-outlined-btn'
        onClick={() => setOpen(true)}
        disabled={saving}
      >
        + Add Voucher
      </button>

      <ModalWrapper open={open} onClose={() => (saving ? {} : setOpen(false))} maxWidth='md'>
        <FormProvider {...methods}>
          <form className='column gap-1' onSubmit={handleSubmit(submitVoucherRequest)}>
            <SectionTitle title='Request voucher expense' />

            <ControlledSelect
              id='expenseType'
              name='expenseType'
              label='Expense type*'
              placeholder='Select expense type'
              rules={voucherRequestValidation.expenseType}
              fullWidth
              options={EXPENSE_TYPE_OPTIONS}
              reserveErrorSpace
            />

            <div className='row gap-5'>
              <ControlledTextField
                id='amountRequested'
                name='amountRequested'
                type='text'
                inputMode='numeric'
                label='Amount*'
                placeholder='Enter amount'
                rules={voucherRequestValidation.amountRequested}
                prefix='₹'
                hideArrow
                reserveErrorSpace
              />
              <ControlledDatePicker
                id='dateOfEvent'
                name='dateOfEvent'
                label='Date of event*'
                disableFuture
                maxDate={dayjs()}
                rules={voucherRequestValidation.dateOfEvent}
                reserveErrorSpace
              />
            </div>

            <ControlledTextField
              id='employeeNote'
              name='employeeNote'
              isMultiline
              rows={3}
              label='Note (if any)'
              fullWidth
              rules={voucherRequestValidation.employeeNote}
              showCharCount
            />

            <ControlledFileUpload
              name='attachment'
              rules={voucherRequestValidation.attachment}
              autoUpload={false}
              buttonText='Attach file'
              buttonIcon='ion:attach-outline'
              buttonClass='btn text-btn text-btn--medium underline'
              showPreview
              previewPosition='after'
              previewText='view'
              accept='.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/*'
            />

            <div className='row row--end gap-3'>
              <button
                type='button'
                className='btn outlined-btn'
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button type='submit' className='btn cta-btn' disabled={saving}>
                Save
              </button>
            </div>
          </form>
        </FormProvider>
      </ModalWrapper>
    </>
  );
}
